import { Dispatch } from 'redux';
import { submissionActionTypes } from './actionTypes/submission';
import {
  MgState,
  UpdateSubmissionsGroupRequestInterface,
  UpdateSubmissionsTypeRequestInterface,
  CreateSubmissionRequestInterface,
  DeleteSubmissionsRequestInterface,
  SubmissionEntityInterface,
  SubmissionQueryInterface,
  OriginBlockEntityInterface,
  UpdateSubmissionRequestInterface,
  OriginEntitySectionsOriginSectionEntityInterface,
} from '@types';
import { getSubmissionGroupName, handleDate, shouldUpdateGroups } from '@utils';
import { failure, loading, totalItems } from './commonActions';
import { setNotification } from '@actions/appActions';
import {
  clearSubmissionCacheValuesByOriginId,
  createSubmission,
  deleteSubmissions,
  getAllSubmissions,
  getSubmissionById,
  reassignSubmissionsGroup,
  updateSubmission,
  updateSubmissionsType,
} from '@services/submissionService';
import {
  setIsOpenChangeTypeModal,
  setIsOpenDeleteModal,
  setIsOpenMultipleDeleteModal,
  setIsOpenReassignGroupModal,
  setIsOpenSaveAsModal,
  setPartialFilters,
} from '@actions/lettersPageActions';
import { SubmissionTypes, OriginBlockFormValueKey } from '@enums';
import {
  setSubmissionAttachments,
  setSubmissionContext,
} from '@actions/submissionEnvironmentActions';
import { makeSubmissionGroups } from '@actions/submissionGroupsActions';
import _cloneDeep from 'lodash/cloneDeep';
import { createNewSectionsAndBlocksByOriginId } from '@services/originService';
import {
  UpdateCurrentSubmissionOriginSectionNameActionPayloadType,
  CreateSampleInterface,
} from '@types';

export const setSubmissions = (submissions: SubmissionEntityInterface[]) => {
  return {
    type: submissionActionTypes.SET_SUBMISSIONS,
    payload: submissions,
  };
};

export const setCheckedSubmissions = (
  submissionIds: string[]
): { type: submissionActionTypes; payload: string[] } => {
  return {
    type: submissionActionTypes.SET_CHECKED_SUBMISSIONS,
    payload: submissionIds,
  };
};

export const setCurrentSubmissionInitialTitle = (title: string) => {
  return {
    type: submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_TITLE,
    payload: title,
  };
};

export const setCurrentSubmissionInitialDescription = (description: string) => {
  return {
    type: submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_DESCRIPTION,
    payload: description,
  };
};

export const setCurrentSubmissionInitialData = (data: string) => {
  return {
    type: submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_DATA,
    payload: data,
  };
};

export const setCurrentSubmission = (currentSubmission: SubmissionEntityInterface) => {
  return {
    type: submissionActionTypes.SET_CURRENT_SUBMISSION,
    payload: _cloneDeep(currentSubmission),
  };
};

export const setNavigateToSubmission = (id: string) => {
  return {
    type: submissionActionTypes.NAVIGATE_TO_SUBMISSION,
    payload: id,
  };
};

export const setCurrentSubmissionTemplateEditorMode = (isActive: boolean) => {
  return {
    type: submissionActionTypes.SET_TEMPLATE_EDITOR_MODE,
    payload: isActive,
  };
};

export const updateCurrentSubmissionOriginSectionName = (
  originSectionWithOrder: UpdateCurrentSubmissionOriginSectionNameActionPayloadType
) => {
  return {
    type: submissionActionTypes.UPDATE_CURRENT_SUBMISSION_ORIGIN_SECTION_NAME,
    payload: originSectionWithOrder,
  };
};

export const updateCurrentSubmissionOriginSectionBlock = (
  originSectionWithOrderId: string,
  originSectionBlockWithOrderId: string,
  updatedBlockFields: Partial<OriginBlockEntityInterface>,
  valueKey: OriginBlockFormValueKey
) => {
  return {
    type: submissionActionTypes.UPDATE_ORIGIN_SECTION_BLOCK,
    payload: {
      originSectionWithOrderId,
      originSectionBlockWithOrderId,
      updatedBlockFields,
      valueKey,
    },
  };
};

export const deleteCurrentSubmissionOriginSectionBlock = (
  originSectionWithOrderId: string,
  originBlockId: string
) => {
  return {
    type: submissionActionTypes.DELETE_ORIGIN_SECTION_BLOCK,
    payload: {
      originSectionWithOrderId,
      originBlockId,
    },
  };
};

export const createSubmissionOriginSectionBlock = (originSectionWithOrderId: string) => {
  return {
    type: submissionActionTypes.CREATE_ORIGIN_SECTION_BLOCK,
    payload: {
      originSectionWithOrderId,
    },
  };
};

export const setCurrentSubmissionDefaultOriginSections = (
  originSections: OriginEntitySectionsOriginSectionEntityInterface[]
) => {
  return {
    type: submissionActionTypes.SET_CURRENT_SUBMISSION_DEFAULT_ORIGIN_SECTIONS,
    payload: originSections,
  };
};

export const resetUnsavedChangesTemplateEditorForm = () => {
  return {
    type: submissionActionTypes.RESET_UNSAVED_CHANGES_TEMPLATE_EDITOR_FORM,
  };
};

export const deleteCurrentSubmissionOriginSection = (originSectionId: string) => {
  return {
    type: submissionActionTypes.DELETE_ORIGIN_SECTION,
    payload: {
      originSectionId,
    },
  };
};

export const createSubmissionOriginSection = (sectionName: string) => {
  return {
    type: submissionActionTypes.CREATE_ORIGIN_SECTION,
    payload: {
      sectionName,
    },
  };
};

export const updateCurrentSubmissionOriginSectionBlocksOrder = (
  startIndex: number,
  endIndex: number,
  originSectionWithOrderId: string
) => {
  return {
    type: submissionActionTypes.UPDATE_ORIGIN_SECTION_BLOCKS_ORDER,
    payload: { startIndex, endIndex, originSectionWithOrderId },
  };
};

export const updateCurrentSubmissionOriginSectionsOrder = (
  startIndex: number,
  endIndex: number
) => {
  return {
    type: submissionActionTypes.UPDATE_ORIGIN_SECTIONS_ORDER,
    payload: { startIndex, endIndex },
  };
};

export const updateCurrentSubmissionTemplateEditorFormChangedFlag = (flag: boolean) => {
  return {
    type: submissionActionTypes.UPDATE_CURRENT_SUBMISSION_TEMPLATE_EDITOR_FORM_CHANGED_FLAG,
    payload: { flag },
  };
};

let cachedQuery;

export const makeSubmissions = (
  query?: SubmissionQueryInterface,
  previousQuery?: boolean,
  redirect?: boolean,
  redirectId?: string
) => {
  let resultQuery;

  if (previousQuery) {
    resultQuery = cachedQuery;
  } else {
    resultQuery = query;
    cachedQuery = query;
  }
  const instanceName = 'submissions';
  return async (dispatch) => {
    try {
      const { items, total } = await getAllSubmissions(resultQuery);
      dispatch(totalItems(total, instanceName));
      dispatch(setSubmissions(items));

      if (!items.length) {
        dispatch(setCurrentSubmission(null));
      }

      if (items[0]?.id && redirect) {
        dispatch(setNavigateToSubmission(redirectId || items[0].id));
      }
    } catch (err) {
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeCurrentSubmission = (id: string) => {
  const instanceName = 'current_submission';

  return async (dispatch: Dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      const submission = await getSubmissionById(id);
      dispatch(setSubmissionContext(submission.data));
      dispatch(setSubmissionAttachments(submission.origin?.form?.attachments));
      const currentSubmission = handleDate(submission);
      dispatch(setCurrentSubmission(currentSubmission));
      /**
       * Setting initial state to check if the data changed
       */
      dispatch(setCurrentSubmissionInitialData(currentSubmission.data));
      dispatch(setCurrentSubmissionInitialTitle(currentSubmission.title));
      dispatch(setCurrentSubmissionInitialDescription(currentSubmission.description));
      dispatch(setCurrentSubmissionDefaultOriginSections(currentSubmission.origin.originSections));
      dispatch(updateCurrentSubmissionTemplateEditorFormChangedFlag(false));
    } catch (err) {
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeDeleteSubmissions = (data: DeleteSubmissionsRequestInterface) => {
  const instanceName = 'delete_submissions';

  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      await deleteSubmissions(data);
      dispatch(setCheckedSubmissions([]));
      dispatch(makeSubmissions(null, true, true));
      dispatch(setIsOpenDeleteModal(false));
      dispatch(setIsOpenMultipleDeleteModal(false));
      dispatch(makeSubmissionGroups());

      dispatch(
        setNotification({
          textKey:
            data.submissionIds.length > 1
              ? 'templates_success_deleted'
              : 'template_success_deleted',
          variant: 'success',
        })
      );

      dispatch(setCurrentSubmission(null));
    } catch (err) {
      dispatch(
        setNotification({
          textKey:
            data.submissionIds.length > 1 ? 'templates_error_deleted' : 'template_error_deleted',
          variant: 'error',
        })
      );

      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeCreateSubmission = (
  data: CreateSubmissionRequestInterface | CreateSampleInterface,
  isSample = false
) => {
  const instanceName = 'create_submission';
  const { isCommon, groupId } = data;
  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      const { id } = await createSubmission(data, isSample);
      dispatch(setIsOpenSaveAsModal(false));
      dispatch(
        setPartialFilters(
          {
            submissionType: isCommon ? SubmissionTypes.Common : SubmissionTypes.Own,
            groupId,
            title: '',
          },
          null,
          true,
          id
        )
      );
      if (shouldUpdateGroups(groupId)) {
        dispatch(makeSubmissionGroups());
      }
      dispatch(setCheckedSubmissions([]));
      dispatch(setNotification({ textKey: 'template_success_created', variant: 'success' }));
    } catch (err) {
      dispatch(failure(true, instanceName));
      dispatch(setNotification({ textKey: 'template_error_created', variant: 'error' }));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeUpdateSubmission = (id: string, data: UpdateSubmissionRequestInterface) => {
  const instanceName = 'update_submission';

  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      const submission = await updateSubmission(id, data);
      dispatch(makeSubmissions(null, true, false));
      dispatch(
        setNotification({ textKey: 'template_success_updated', variant: 'success', duration: 3000 })
      );
      const currentSubmission = handleDate(submission);
      dispatch(setCurrentSubmission(currentSubmission));
      /**
       * Setting initial state to check if the data changed
       */
      dispatch(setCurrentSubmissionInitialData(currentSubmission.data));
      dispatch(setCurrentSubmissionInitialTitle(currentSubmission.title));
      dispatch(setCurrentSubmissionInitialDescription(currentSubmission.description));
    } catch (err) {
      setNotification({ textKey: 'template_error_updated', variant: 'error', duration: 3000 });
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeReassignSubmissionsGroup = (data: UpdateSubmissionsGroupRequestInterface) => {
  const instanceName = 'reassign_submissions_group';
  const { submissionIds, groupId } = data;
  let groupName: string;
  return async (dispatch, getState) => {
    try {
      const store: MgState = getState();
      groupName = getSubmissionGroupName(store.submissionGroup.submissionGroups, groupId);
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      await reassignSubmissionsGroup(data);
      dispatch(makeSubmissionGroups());
      dispatch(setCheckedSubmissions([]));
      dispatch(setIsOpenReassignGroupModal(false));
      dispatch(makeSubmissions(null, true));

      dispatch(
        setNotification({
          textKey:
            submissionIds.length > 1
              ? 'templates_success_reassigned_group'
              : 'template_success_reassigned_group',
          additionalText: groupName,
          variant: 'success',
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          textKey:
            submissionIds.length > 1
              ? 'templates_error_reassigned_group'
              : 'template_error_reassigned_group',
          additionalText: groupName,
          variant: 'error',
        })
      );
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeChangeSubmissionsType = (data: UpdateSubmissionsTypeRequestInterface) => {
  const instanceName = 'change_submissions_type';
  const { submissionIds } = data;
  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));

      await updateSubmissionsType(data);
      dispatch(setCheckedSubmissions([]));
      dispatch(setIsOpenChangeTypeModal(false));
      dispatch(makeSubmissions(null, true));

      dispatch(
        setNotification({
          textKey:
            submissionIds.length > 1
              ? 'templates_success_reassigned'
              : 'template_success_reassigned',
          variant: 'success',
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          textKey:
            submissionIds.length > 1 ? 'templates_error_reassigned' : 'template_error_reassigned',
          variant: 'error',
        })
      );
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const createNewSectionsAndBlocksByCurrentSubmissionOriginId = () => {
  const instanceName = 'current_submission';
  return async (dispatch, getState) => {
    try {
      const store: MgState = getState();
      const currentSubmission = store.submission.currentSubmission;

      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));

      await createNewSectionsAndBlocksByOriginId(
        currentSubmission.origin.id,
        currentSubmission.origin
      );

      clearSubmissionCacheValuesByOriginId(currentSubmission.origin.id);
      const submission = await getSubmissionById(currentSubmission.id, false);

      dispatch(setSubmissionContext(submission.data));
      dispatch(setCurrentSubmission(handleDate(submission)));
      dispatch(setCurrentSubmissionDefaultOriginSections(submission.origin.originSections));
      dispatch(updateCurrentSubmissionTemplateEditorFormChangedFlag(false));
      dispatch(
        setNotification({
          textKey: 'update_sample_template_success',
          variant: 'success',
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          textKey: 'update_sample_template_error',
          variant: 'error',
        })
      );
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};
